import { mean } from "lodash-es";
import { overlay } from "overlay-kit";

import type {
  OutcomeAssessmentType,
  PageGroupUserType,
  PageType,
  SubmissionType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { filterMap } from "utils/collections";
import { isGroupPage } from "utils/page-utils";

export const LAST_ASSESSMENT_PANEL_ACCESS_ID = "last-panel";

export const showUnpublishAssessmentModal = (count: number, onUnpublishAll: () => void) => {
  let confirmed = false;
  return overlay.openAsync<boolean>(({ isOpen, close }) => (
    <ConfirmationDialog
      confirmButtonText={t("assessment.unpublish_all")}
      description={t("assessment.unpublish_assessment_modal.description", { count })}
      open={isOpen}
      title={t("assessment.unpublish_assessment_modal.title")}
      onClose={() => close(confirmed)}
      onConfirm={() => {
        confirmed = true;
        onUnpublishAll();
      }}
    />
  ));
};

export const getMeanScoreByPageOutcomeId = (outcomeAssessments: OutcomeAssessmentType[]) => {
  const scoresByPageOutcomeId: { [pageOutcomeId: string]: number[] } = {};
  for (const oa of outcomeAssessments) {
    (scoresByPageOutcomeId[oa.page_outcome_id] ||= []).push(oa.score);
  }

  const meanScoreByPageOutcomeId: { [pageOutcomeId: string]: number } = {};
  for (const [pageOutcomeId, scores] of Object.entries(scoresByPageOutcomeId)) {
    meanScoreByPageOutcomeId[pageOutcomeId] = mean(scores);
  }
  return meanScoreByPageOutcomeId;
};

export const getTotalSubmissions = (
  page: PageType,
  submissions: SubmissionType[],
  enabledPageGroupUsers: PageGroupUserType[] = [],
) => {
  if (isGroupPage(page)) {
    // Get unique page_group_ids that have enabled users
    const groupsWithEnabledUsers = new Set(enabledPageGroupUsers.map((pgu) => pgu.page_group_id));

    // Only count groups that have submissions AND have enabled users
    return new Set(
      filterMap(
        submissions,
        (submission) => groupsWithEnabledUsers.has(submission.page_group_id!),
        (submission) => submission.page_group_id,
      ),
    ).size;
  }

  return new Set(submissions.map((s) => s.section_user_id)).size;
};
